import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/Home.css';

const Home: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const featuresRef = useRef<HTMLElement>(null);
  const deployRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // 模拟内容加载
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 处理 URL 参数和 hash
    const handleScroll = () => {
      console.log('Home组件: 处理滚动');
      console.log('当前location:', location);
      
      const searchParams = new URLSearchParams(location.search);
      const scrollTo = searchParams.get('scrollTo');
      const hash = location.hash.replace('#', '');
      
      console.log('scrollTo:', scrollTo);
      console.log('hash:', hash);
      
      if (scrollTo === 'pricing' || hash === 'pricing') {
        console.log('需要滚动到pricing部分');
        
        // 使用轮询检查元素是否存在
        const checkElement = () => {
          console.log('检查pricing部分是否存在');
          const pricingSection = document.getElementById('pricing');
          if (pricingSection) {
            console.log('找到pricing部分，执行滚动');
            pricingSection.scrollIntoView({ behavior: 'smooth' });
          } else {
            console.log('pricing部分未找到，1秒后重试');
            setTimeout(checkElement, 1000);
          }
        };

        // 给页面一些时间加载
        setTimeout(checkElement, 500);
      }
    };

    if (!isLoading) {
      handleScroll();
    }
  }, [location, isLoading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }
    if (deployRef.current) {
      observer.observe(deployRef.current);
    }
    if (pricingRef.current) {
      observer.observe(pricingRef.current);
    }

    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
      if (deployRef.current) {
        observer.unobserve(deployRef.current);
      }
      if (pricingRef.current) {
        observer.unobserve(pricingRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            <span>{t('hero.title.line1')}</span>
            <span>{t('hero.title.line2')}</span>
            <span>{t('hero.title.line3')}</span>
          </h1>
          <p className="hero-subtitle">
            {t('hero.subtitle')}
          </p>
          <div className="hero-actions">
            <Link to="/apps" className="cta-button">
              {t('hero.cta.start')}
            </Link>
            <button className="demo-button">
              {t('hero.cta.demo')}
            </button>
          </div>
        </div>
        <div className="hero-background">
          <div className="gradient-overlay"></div>
          <div className="dots-pattern"></div>
        </div>
      </section>

      {/* Features Introduction */}
      <section className="features-intro">
        <div className="feature-block">
          <div className="feature-content">
            <h2>了解并了解您的应用</h2>
            <p>分享您的应用描述和需求，我们的代理会深入理解您的目标。以开始使用。Ark App Builder将帮助您明确定位和规划。</p>
          </div>
          <div className="feature-image">
            <div className="chat-bubble">
              <div className="chat-message">
                <span className="message-text">问我什么？</span>
                <span className="message-description">让我帮助您构建应用，解决您的需求</span>
              </div>
            </div>
            <div className="chat-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </div>

        <div className="feature-block">
          <div className="feature-content">
            <h2>创建高级计划</h2>
            <p>探索创新的开发方法，分解为引人入胜的操作步骤。智能建议使您能开发有前途的项目流程，该计划会随着您的反馈不断发展。</p>
          </div>
          <div className="feature-image">
            <div className="task-list">
              <div className="task-item">
                <span className="task-status new">新建</span>
                <span className="task-name">自建登录页面</span>
              </div>
              <div className="task-item">
                <span className="task-status done">完成</span>
                <span className="task-name">使用Google实现登录</span>
              </div>
              <div className="task-item">
                <span className="task-status todo">待办</span>
                <span className="task-name">构建管理页面</span>
              </div>
            </div>
          </div>
        </div>

        <div className="feature-block">
          <div className="feature-content">
            <h2>执行大任务</h2>
          <p>只需点击开始任务。代理会思考、研究、编码，并在需要输入时通知您。开启源码人工对话。</p>
          </div>
          <div className="feature-image">
            <div className="task-progress">
              <div className="task-item in-progress">
                <span className="task-status">进行中</span>
                <span className="task-name">使用Google实现登录</span>
              </div>
              <button className="complete-button">完成配置</button>
            </div>
          </div>
        </div>

        <div className="feature-block">
          <div className="feature-content">
            <h2>取得技术所有权</h2>
            <p>任意分析您需要的技术计划和技术栈。根据您的需求和预算，您可以随意调整。</p>
          </div>
          <div className="feature-image">
            <Link to="/apps" className="deploy-now-button">
              🚀 即刻部署
            </Link>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section ref={featuresRef} className="features-section">
        <h2 className="section-title">{t('features.title')}</h2>
        
        <div className="features-grid">
          <div className="feature-card">
            <h3>{t('features.understand.title')}</h3>
            <p>{t('features.understand.description')}</p>
            <div className="feature-files">
              <div className="file-item">📄 spec.pdf</div>
              <div className="file-item">🎨 mockup.jpg</div>
              <div className="file-item">📱 screenshot.png</div>
            </div>
          </div>

          <div className="feature-card">
            <h3>{t('features.plan.title')}</h3>
            <p>{t('features.plan.description')}</p>
            <div className="task-list">
              <div className="task-item recommended">
                <span className="task-status">推荐</span>
                <span className="task-name">创建一个简洁大胆风格的着陆页</span>
              </div>
              <div className="task-item">
                <span className="task-status">待办</span>
                <span className="task-name">使用 Firebase 添加 Google 登录</span>
              </div>
              <div className="task-item">
                <span className="task-status">待办</span>
                <span className="task-name">使用 Airtable API 创建管道视图</span>
              </div>
            </div>
          </div>

          <div className="feature-card">
            <h3>{t('features.execute.title')}</h3>
            <p>{t('features.execute.description')}</p>
            <div className="task-progress">
              <div className="task-item in-progress">
                <span className="task-status">进行中</span>
                <span className="task-name">实现 Google 登录</span>
              </div>
            </div>
          </div>

          <div className="feature-card">
            <h3>{t('features.own.title')}</h3>
            <p>{t('features.own.description')}</p>
            <Link to="/apps" className="deploy-button">
              🚀 部署到生产环境
            </Link>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      {/* <section className="collab-section">
        <h2 className="section-title">
          停止指挥。<br />
          开始合作。
        </h2>
        <div className="collab-background">
          <div className="gradient-circle"></div>
        </div>
      </section> */}

      {/* Deploy Section */}
      <section ref={deployRef} className="deploy-section">
        <div className="deploy-content">
          <h2 className="section-title">在几秒钟内部署到您自己的域名</h2>
          <p className="section-description">
            Ark App Builder 代理将自动处理所有部署细节，
            确保您的应用运行在高性能、可扩展的云基础设施上。无需担心技术细节，
            专注于您的业务发展。
          </p>
          <Link to="/apps" className="start-button">
            开始使用
          </Link>
        </div>
        <div className="deploy-visual">
          <div className="particles"></div>
          <div className="gradient-overlay"></div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingRef} id="pricing" className="pricing-section">
        <h2 className="section-title">{t('pricing.title')}</h2>
        <p className="section-description">{t('pricing.description')}</p>
        
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>{t('pricing.plans.community.title')}</h3>
            <p className="price">{t('pricing.plans.community.price')}<span>{t('pricing.plans.community.period')}</span></p>
            <p className="description">{t('pricing.plans.community.description')}</p>
            <ul className="features-list">
              {(() => {
                const features = t('pricing.plans.community.features', { returnObjects: true });
                return Array.isArray(features)
                  ? features.map((feature: any, index: number) => (
                      <li key={index}>{typeof feature === 'string' ? feature : JSON.stringify(feature)}</li>
                    ))
                  : null;
              })()}
            </ul>
            <Link to="/apps" className="pricing-cta">{t('auth.signup')}</Link>
          </div>

          <div className="pricing-card featured">
            <h3>{t('pricing.plans.pro.title')}</h3>
            <p className="price">{t('pricing.plans.pro.price')}<span>{t('pricing.plans.pro.period')}</span></p>
            <p className="description">{t('pricing.plans.pro.description')}</p>
            <ul className="features-list">
              {(() => {
                const features = t('pricing.plans.pro.features', { returnObjects: true });
                return Array.isArray(features)
                  ? features.map((feature: any, index: number) => (
                      <li key={index}>{typeof feature === 'string' ? feature : JSON.stringify(feature)}</li>
                    ))
                  : null;
              })()}
            </ul>
            <Link to="/apps" className="pricing-cta">{t('auth.signup')}</Link>
          </div>

          <div className="pricing-card">
            <h3>{t('pricing.plans.enterprise.title')}</h3>
            <p className="price">{t('pricing.plans.enterprise.price')}<span>{t('pricing.plans.enterprise.period')}</span></p>
            <p className="description">{t('pricing.plans.enterprise.description')}</p>
            <ul className="features-list">
              {(() => {
                const features = t('pricing.plans.enterprise.features', { returnObjects: true });
                return Array.isArray(features)
                  ? features.map((feature: any, index: number) => (
                      <li key={index}>{typeof feature === 'string' ? feature : JSON.stringify(feature)}</li>
                    ))
                  : null;
              })()}
            </ul>
            <Link to="/apps" className="pricing-cta">{t('auth.signup')}</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home; 